import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  GroupStateContext,
  type GroupStateContextValue,
  type GroupId,
  type GroupSharedState,
} from "@pulse-ui/widget-sdk";
import { selectAllGroupStates, updateGroupState } from "./groupsSlice";
import type { AppDispatch } from "@/store";

const DEFAULT_STATE: GroupSharedState = {};

/**
 * Provides group shared state backed by the Redux groupsSlice.
 * Mount this once near the app root (inside the Redux Provider).
 * All widgets under this provider will read/write group state via Redux,
 * giving DevTools visibility and redux-persist support.
 */
export function ReduxGroupStateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const groupStates = useSelector(selectAllGroupStates);
  const dispatch = useDispatch<AppDispatch>();

  const ctx = useMemo<GroupStateContextValue>(
    () => ({
      getGroupState: (groupId: GroupId) =>
        groupStates[groupId] ?? DEFAULT_STATE,
      setGroupState: (
        groupId: GroupId,
        updater: (prev: GroupSharedState) => GroupSharedState
      ) => {
        const current = groupStates[groupId] ?? DEFAULT_STATE;
        dispatch(updateGroupState({ groupId, state: updater(current) }));
      },
    }),
    [groupStates, dispatch]
  );

  return (
    <GroupStateContext.Provider value={ctx}>
      {children}
    </GroupStateContext.Provider>
  );
}
